import { useCallback, useEffect, useState } from 'react';
import { ArchiveRestore, Film, Trash2 } from 'lucide-react';
import { api } from '../../lib/api';
import { bytes } from '../../lib/format';
import { useMediaList } from '../../lib/live';
import { toast } from '../../lib/session';
import type { TrashItem } from '../../lib/types';

const days = (iso: string) => Math.max(0, Math.ceil((Date.parse(iso) - Date.now()) / 86_400_000));

/** Deleted titles and removed originals: restorable for 30 days, then deleted automatically. */
export function TrashCard() {
  const [items, setItems] = useState<TrashItem[] | null>(null);
  const [total, setTotal] = useState(0);
  const [busy, setBusy] = useState('');
  const refreshLibrary = useMediaList((s) => s.refresh);
  const items0 = useMediaList((s) => s.items);
  const load = useCallback(() => {
    api.trash().then((r) => {
      setItems(r.items ?? []);
      setTotal(r.bytes);
    }, () => undefined);
  }, []);
  // Reload whenever the library changes (a delete or "remove original" elsewhere on the page).
  const librarySig = `${items0.length}:${items0.filter((m) => m.originalRemoved).length}`;
  useEffect(load, [load, librarySig]);

  const run = async (key: string, fn: () => Promise<unknown>, ok: string) => {
    setBusy(key);
    try {
      await fn();
      toast(ok, 'accent');
      load();
      refreshLibrary();
    } catch (e) {
      toast((e as Error).message, 'red');
    } finally {
      setBusy('');
    }
  };

  return (
    <section className="card" data-anim id="trash">
      <div className="card-head">
        <div style={{ flex: 1 }}>
          <h2 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Trash2 size={17} /> Trash
          </h2>
          <div className="card-sub">
            {items?.length
              ? `${items.length} item${items.length > 1 ? 's' : ''} · ${bytes(total, 1)}. Empty it to free that space.`
              : 'Deleted titles and removed originals stay here for 30 days, then they are deleted automatically.'}
          </div>
        </div>
        {!!items?.some((i) => i.canManage) && (
          <button
            className="btn btn-ghost btn-sm"
            disabled={!!busy}
            onClick={() =>
              confirm(`Empty the Trash? ${bytes(total, 1)} will be deleted for good and can't be recovered.`) &&
              run('all', () => api.emptyTrash(), `Trash emptied. ${bytes(total, 1)} freed`)
            }
          >
            <Trash2 size={13} /> Empty Trash
          </button>
        )}
      </div>
      {items && items.length > 0 && (
        <div className="subs-list">
          {items.map((it) => {
            const key = `${it.kind}/${it.id}`;
            return (
              <div className="subs-row" key={key}>
                <span className="lang-badge" style={{ minWidth: 30 }}>
                  <Film size={14} />
                </span>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div className="cell-main truncate" title={it.fileName}>
                    {it.kind === 'original' ? `Original file of “${it.title}”` : it.title}
                  </div>
                  <div className="cell-sub">
                    {bytes(it.bytes, 1)} · {it.kind === 'original' ? 'the title still plays from its copy' : `deleted${it.by ? ` by ${it.by}` : ''}`} ·{' '}
                    {days(it.expiresAt)} days left
                  </div>
                </div>
                {it.canManage && (
                  <>
                    <button className="btn btn-ghost btn-sm" disabled={!!busy} onClick={() => run(key, () => api.restoreTrash(it.kind, it.id), `Restored “${it.title}”`)}>
                      <ArchiveRestore size={13} /> {busy === key ? '…' : 'Restore'}
                    </button>
                    <button
                      className="icon-btn"
                      aria-label="Delete for good"
                      title="Delete for good"
                      disabled={!!busy}
                      onClick={() =>
                        confirm(`Delete ${it.kind === 'original' ? 'the original file of ' : ''}“${it.title}” for good? This can't be undone.`) &&
                        run(key, () => api.purgeTrash(it.kind, it.id), `Deleted for good. ${bytes(it.bytes, 1)} freed`)
                      }
                    >
                      <Trash2 size={14} />
                    </button>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
